// Company (enterprise account, e.g. Spie) and its tenants (named subsets of its projects).
// Unrelated to the free-text `User.company` field.
import { AuthMethod } from './user';

export interface NamedRef {
    id: string;
    name: string;
}

// A tenant project of the company detail: its name and position (project map).
export interface TenantProject extends NamedRef {
    city?: string;
    lat?: number;
    lon?: number;
}

// Reports really sent by a project: count per frequency, distinct recipients.
export interface ReportDiffusion {
    daily: number;
    weekly: number;
    monthly: number;
    recipients: number;
}

// Project summary of the company detail.
export interface CompanyProject extends NamedRef {
    database?: string;
    picture?: string;   // URL
    city?: string;
    country?: string;
    lat?: number;
    lon?: number;
    startDate?: string;
    endDate?: string;
    archived?: boolean;
    frozen?: boolean;
    reference?: string;         // n° d'affaire
    reportsEnabled?: boolean;
    membersCount?: number;
    diffusion?: ReportDiffusion;
    aiModules?: string[];
    stream?: { played30Days: number; dailyLimit: number };   // seconds; limit 0 = unlimited
}

export interface CompanyUser {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    avatar?: string;
    role?: NamedRef;
    ssoEnabled?: boolean;   // SSO allowed by the admin
    ssoLinked?: boolean;    // account linked to auth-api
    mfaEnabled?: boolean;   // MFA allowed by the admin (enrolment itself lives in auth-api)
    allowedAuthMethods?: AuthMethod[];
    lastConnection?: string;
    verified?: boolean;
}

// P: project ids, or project refs once populated (company detail).
export interface Tenant<P = string> {
    id: string;
    name: string;
    icon: string;   // Material icon name
    color: string;  // #rrggbb
    projects: P[];
    createdBy?: string;
    createdDate?: string;
    updatedDate?: string;
}

export interface Company<P = string, U = string> {
    id: string;
    name: string;
    logo?: string;          // URL
    allProjects: boolean;   // CAD.42: every project and every dev/admin, implicitly
    projects: P[];
    users: U[];
    tenants: Tenant<P>[];
    createdBy?: string;
    createdDate?: string;
    updatedDate?: string;
}

// GET /companies/:id — CAD.42 comes with empty lists, its counts cover everything.
export interface CompanyDetail extends Omit<Company<CompanyProject, CompanyUser>, 'createdBy'> {
    createdBy?: Pick<CompanyUser, 'id' | 'firstName' | 'lastName'>;
    projectsCount: number;
    usersCount: number;
}

// GET /companies/searchAndCount row.
export type CompanySummary = Omit<Company, 'projects' | 'users' | 'tenants'> & {
    projectsCount: number;
    usersCount: number;
    tenantsCount: number;
};

export interface CompanySearchResult {
    results: CompanySummary[];
    total: number;
}

// GET /companies/mine, and what the current company store keeps.
export type CompanyLight = Pick<Company, 'id' | 'name' | 'logo' | 'allProjects'>;

// `logo`: a data:image base64 string uploads it, '' removes it.
export interface PostCompany {
    name: string;
    logo?: string;
}

export interface PutCompany extends Partial<PostCompany> {
    id: string;
}

export interface PostTenant {
    name: string;
    icon: string;
    color: string;
    projects?: string[];
}

export interface PutTenant extends Partial<PostTenant> {
    id: string;
}

export const defaultTenant: PostTenant = { name: '', icon: 'apartment', color: '#1E88E5', projects: [] };
